<template>
  <v-card :outlined="!embedded" :flat="embedded" :color="embedded ? 'transparent' : undefined" class="mb-4">
    <v-card-title class="py-3" :class="{ 'px-0 pt-0': embedded }">
      <span class="text-body-2 text--secondary">{{ $t('member.absences.help') }}</span>
      <v-spacer />
      <v-btn small outlined color="primary" @click="startAdding">
        <v-icon small left>mdi-plus</v-icon>{{ $t('member.absences.add') }}
      </v-btn>
    </v-card-title>

    <!-- Déclarer : une période (jours inclus, un seul jour = deux clics sur le même) et une note facultative -->
    <v-dialog v-model="adding" max-width="420">
      <v-form ref="form" @submit.prevent="save">
        <v-card>
          <v-card-title class="text-h6">{{ $t('member.absences.add') }}</v-card-title>
          <v-card-text>
            <date-picker
                v-model="form.period"
                range
                :label="$t('member.absences.period-label')"
                prepend-icon="mdi-calendar-range"
                :hint="$t('member.absences.period-hint')"
            />
            <v-text-field v-model="form.note" :label="$t('member.absences.note-optional')" prepend-icon="mdi-note-text-outline" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn small text :disabled="saving" @click="adding = false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" type="submit" :loading="saving" :disabled="!canSave">{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="10"
        :hide-default-footer="items.length <= 10"
        :no-data-text="$t('member.absences.none')"
        dense
    >
      <template #item.period="{ item }">
        <span v-if="item.from === item.to">{{ $t('member.absences.on', { date: formatDay(item.from) }) }}</span>
        <span v-else>{{ $t('member.absences.period', { from: formatDay(item.from), to: formatDay(item.to) }) }}</span>
      </template>
      <template #item.note="{ item }">
        <span v-if="item.note">{{ item.note }}</span>
        <span v-else class="text--disabled">—</span>
      </template>
      <template #item.declaredBy="{ item }">
        {{ $t(`member.absences.declared-by.${item.declaredBy}`) }}
        <span v-if="item.declaredByName" class="text--secondary">({{ item.declaredByName }})</span>
        <div v-if="item.createdAt" class="text-caption text--secondary">{{ item.createdAt | formatDate }}</div>
      </template>
      <template #item.actions="{ item }">
        <v-btn v-if="item.deletable" icon small :disabled="deleting === item.id" @click="remove(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import dayjs from 'dayjs'
import MemberAbsenceRepository from '@repository/MemberAbsenceRepository'

/**
 * Sur la fiche d'un membre : ses absences (vacances…), déclarées par lui ou par son gestionnaire.
 * Ses recharges à domicile pendant ces jours ne sont pas remboursées. Les refus viennent du serveur.
 */
export default {
  name: 'MemberAbsences',

  props: {
    member: { type: Object, required: true },
    /** Dans la fenêtre d'un bouton : ni cadre ni titre (la fenêtre a le sien). */
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      items: [],
      loading: false,
      adding: false,
      saving: false,
      deleting: null,
      form: { period: [], note: '' },
    }
  },

  computed: {
    organizationId() {
      return this.member?.organization?.id || null
    },
    canSave() {
      return (this.form.period || []).length === 2
    },
    headers() {
      return [
        { text: this.$t('member.absences.period-label'), value: 'period', sortable: false },
        { text: this.$t('member.absences.note'), value: 'note', sortable: false },
        { text: this.$t('member.absences.declared-by-label'), value: 'declaredBy', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 60 },
      ]
    },
  },

  watch: {
    'member.id': {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },

  methods: {
    load() {
      if (!this.organizationId || !this.member?.id) return
      this.loading = true
      MemberAbsenceRepository.list(this.organizationId, this.member.id)
        .then(items => {
          this.items = [...(items || [])].sort((a, b) => (b.from || '').localeCompare(a.from || ''))
          this.$emit('counts', { total: this.items.length, current: this.items.filter(this.isCurrentOrComing).length })
        })
        .catch(err => this.notifyError(err))
        .finally(() => { this.loading = false })
    },

    startAdding() {
      this.form = { period: [], note: '' }
      this.adding = true
    },

    save() {
      if (!this.canSave) return
      this.saving = true
      const note = (this.form.note || '').trim() || null
      const [from, to] = [...this.form.period].sort()
      MemberAbsenceRepository.create(this.organizationId, this.member.id, { from, to, note })
        .then(() => {
          this.adding = false
          this.notifySuccess(this.$t('member.absences.added'))
          this.load()
        })
        .catch(err => this.notifyError(err))
        .finally(() => { this.saving = false })
    },

    remove(item) {
      this.$dialog.confirm({ text: this.$t('member.absences.delete-confirm') }).then(result => {
        if (!result) return
        this.deleting = item.id
        MemberAbsenceRepository.delete(this.organizationId, this.member.id, item.id)
          .then(() => {
            this.notifySuccess(this.$t('member.absences.deleted'))
            this.load()
          })
          .catch(err => this.notifyError(err))
          .finally(() => { this.deleting = null })
      })
    },

    /** En cours ou à venir : ce que compte le bouton de la fiche. */
    isCurrentOrComing(item) {
      return !!item.to && item.to >= dayjs().format('YYYY-MM-DD')
    },

    formatDay(day) {
      return day ? dayjs(day).format('DD/MM/YYYY') : '—'
    },
  },
}
</script>
